import React, { useState } from 'react';
import { Modal } from '@ofself/shell';
import { Facts, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { useConfirm } from '../../components/nucleus/actions';
import { importPublicKey, encryptFieldV2 } from '../../utils/fieldEncryption';
import { ago } from '../../nucleus/format';
import { Field, blank, typed } from './fields';
import { pluginsApi } from './data/api';
import { scopeWord } from './data/inspector';
import './plugin-inspector.css';

// A PLUGIN'S SETUP PARTS: its Account, its Credentials and its Settings, each
// once — drawn in a step of the guided setup sheet (SetupSheet.js) and in the
// single-step editor a done Setup row opens. Everything a plugin needs before
// it runs is set here, on ofSelf, never inside an app:
//   Account      — the account ofSelf holds a token for (brokered OAuth)
//   Credentials  — secret fields, encrypted in this browser to the plugin's key;
//                  ofSelf stores ciphertext it cannot read, and never shows one back
//   Settings     — the fields answered once, per tool where the tools differ
// A save the server answers with `consent_required` (202) wants the approval
// next: the parts hand that back to their caller.

/** base64url → bytes. The enc_public_key arrives as 65 raw P-256 bytes. */
function b64urlToBytes(str) {
    const b64 = String(str).replace(/-/g, '+').replace(/_/g, '/');
    return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

/** `{ key: ciphertext }` for what was typed, encrypted to the plugin's key.
 *  The field's name is bound into the key derivation, so ciphertext for one
 *  field cannot be replayed as another. */
export async function encryptValues(entered, publicKey) {
    const pub = await importPublicKey(b64urlToBytes(publicKey));
    const out = {};
    for (const [key, value] of entered) out[key] = await encryptFieldV2(String(value), key, pub);
    return out;
}

// The secret fields across every tool, once each, with the tool that declares
// it (a PUT names a tool that declares the key). Set if any answer says so.
export function credentialFields(configs) {
    const out = [];
    configs.forEach(({ toolId, cfg }) => (cfg?.fields || []).filter((f) => f.secret).forEach((f) => {
        const had = out.find((x) => x.key === f.key);
        if (had) had.is_set = had.is_set || f.is_set;
        else out.push({ ...f, toolId });
    }));
    return out;
}

// The settings (answered once, not secret) per tool, tools with the same
// fields and values standing as one.
export function settingGroups(configs) {
    const groups = [];
    configs.forEach(({ toolId, name, cfg }) => {
        const fields = (cfg?.fields || []).filter((f) => !f.secret && f.scope !== 'runtime');
        if (!fields.length) return;
        const sig = fields.map((f) => `${f.key}=${JSON.stringify(f.value ?? null)}`).join('|');
        const same = groups.find((g) => g.sig === sig);
        if (same) { same.toolIds.push(toolId); same.names.push(name); } else groups.push({ sig, toolIds: [toolId], names: [name], fields });
    });
    return groups;
}

const toolBody = (toolId) => (toolId ? { tool_id: toolId } : {});
/** A save the server held for the approval (202 consent_required). */
export const wantsApproval = (r) => !!(r && (r.consent_required || r.status === 'consent_required'));

/** Encrypt and save what was typed, one save per tool that declares it.
 *  Resolves to { approve } — true when the server wants the approval next. */
export async function saveCredentials(pluginId, groupId, fields, values, publicKey) {
    if (!publicKey) throw new Error('This plugin has no encryption key, so a credential cannot be stored for it safely.');
    const entered = Object.entries(values).filter(([, v]) => !blank(v));
    const byTool = new Map();
    entered.forEach(([k, v]) => {
        const t = fields.find((f) => f.key === k)?.toolId || null;
        byTool.set(t, [...(byTool.get(t) || []), [k, v]]);
    });
    let approve = false;
    for (const [toolId, list] of byTool) {
        // eslint-disable-next-line no-await-in-loop
        const ciphertext = await encryptValues(list, publicKey);
        // eslint-disable-next-line no-await-in-loop
        const r = await pluginsApi.save(pluginId, groupId, { values: ciphertext, ...toolBody(toolId) });
        approve = approve || wantsApproval(r);
    }
    return { approve };
}

/** A settings group's values as the server takes them. */
export function settingParams(group, values) {
    const params = {};
    group.fields.forEach((f) => { const v = values[f.key]; if (!blank(v) || f.type === 'boolean') params[f.key] = typed(f, v); });
    return params;
}

/** Save one settings group for each of its tools. Resolves to { approve }. */
export async function saveSettings(pluginId, groupId, group, values) {
    const params = settingParams(group, values);
    if (!Object.keys(params).length) throw new Error('Nothing to save');
    let approve = false;
    for (const toolId of group.toolIds) {
        // eslint-disable-next-line no-await-in-loop
        const r = await pluginsApi.save(pluginId, groupId, { params, ...toolBody(toolId) });
        approve = approve || wantsApproval(r);
    }
    return { approve };
}

/** A settings group's saved values, as its fields start. */
export const settingValues = (group) => Object.fromEntries(group.fields.map((f) => [f.key, f.value ?? (f.type === 'boolean' ? false : '')]));

/** A field's Clear: a quiet link under it, named for the field. */
function ClearLink({ label, busy, disabled, hint, onClear }) {
    return (
        <button type="button" className="nx-link plugins-clear" disabled={disabled || busy} title={disabled ? hint : undefined}
            aria-label={`Clear ${label}`} onClick={onClear}>
            {busy ? 'Clearing' : 'Clear'}
        </button>
    );
}

// ── the parts ────────────────────────────────────────────────────────────────

/** The account: who it is connected as, what it may reach (in plain words), and Disconnect. */
export function AccountPart({ oauth, gate, onDisconnect }) {
    const conn = oauth?.connection || null;
    const provider = oauth?.provider_label || 'its provider';
    const granted = (conn?.scopes || []).map(scopeWord).filter(Boolean);
    const asked = (oauth?.scopes_requested || []).map(scopeWord).filter(Boolean);
    const scopes = conn?.connected && granted.length ? granted : asked;
    return (
        <>
            <Facts items={[
                { label: 'Account', value: conn?.connected ? `Signed in as ${conn.account_label || 'your account'} ✓` : conn?.revoked ? 'Signed out by the service' : 'Not signed in', tone: conn?.connected ? undefined : 'warn' },
                conn?.connected_at && { label: 'Since', value: ago(conn.connected_at) },
            ]} />
            {conn?.revoked && <Note tone="live">{provider} revoked it{conn.last_error ? `: ${conn.last_error}` : ''}.</Note>}
            {scopes.length > 0 && (
                <div>
                    <span className="plugins-sub">{conn?.connected ? 'It may reach' : 'It will ask for'}</span>
                    {scopes.map((s) => <Row key={s} name={s} />)}
                </div>
            )}
            {conn?.connected && onDisconnect && (
                <div className="plugins-partfoot">
                    <button type="button" className="nx-link plugins-danger-link" disabled={!gate.isAdmin} title={gate.isAdmin ? undefined : gate.hint} onClick={onDisconnect}>
                        Disconnect
                    </button>
                </div>
            )}
        </>
    );
}

/** Ask, then disconnect the account; resolves true once it is gone. */
export function useDisconnect({ pluginId, groupId, name, oauth, onChanged }) {
    const confirm = useConfirm();
    return async () => {
        const conn = oauth?.connection || null;
        const ok = await confirm({
            title: `Disconnect ${conn?.account_label || oauth?.provider_label || 'its account'}?`,
            body: `${name} forgets this account. Its tools stop running automatically until you sign in again.`,
            label: 'Disconnect',
            danger: true,
        });
        if (!ok) return false;
        await pluginsApi.disconnect(pluginId, groupId);
        onChanged?.('Disconnected.');
        return true;
    };
}

/** The credentials: each secret field, typed here and never shown back; Clear for one that is set. */
export function CredentialsPart({ pluginId, groupId, fields, values, onValue, reenter = [], publicKey, gate, onCleared }) {
    const [clearing, setClearing] = useState(null);
    const [error, setError] = useState(null);
    const clear = async (f) => {
        setClearing(f.key); setError(null);
        try {
            await pluginsApi.clear(pluginId, groupId, { credentials: [f.key], toolId: f.toolId || null });
            onCleared?.(`${f.label || f.key} cleared.`);
        } catch (e) { setError(e?.message || 'Could not clear it'); } finally { setClearing(null); }
    };
    if (!publicKey) {
        return <Note tone="live">This plugin has no encryption key, so a credential can’t be stored for it safely. Ask its developer to publish one.</Note>;
    }
    return (
        <>
            {fields.map((f) => {
                const again = reenter.includes(f.key);
                return (
                    <div key={f.key} className="plugins-fieldwrap">
                        <Field field={again ? { ...f, is_set: false } : f} groupId={groupId} value={values[f.key]} disabled={!gate.isAdmin}
                            error={again && blank(values[f.key]) ? 'Enter it again: it was saved for an earlier key.' : null}
                            onChange={(v) => onValue(f.key, v)} />
                        {f.is_set && !again && <ClearLink label={f.label || f.key} busy={clearing === f.key} disabled={!gate.isAdmin} hint={gate.hint} onClear={() => clear(f)} />}
                    </div>
                );
            })}
            {error && <p className="plugins-error" role="alert">{error}</p>}
        </>
    );
}

/** One settings group: its fields, each with Clear once it holds a value. */
export function SettingsPart({ pluginId, groupId, group, heading = null, values, onValue, gate, onCleared }) {
    const [clearing, setClearing] = useState(null);
    const [error, setError] = useState(null);
    const clear = async (f) => {
        setClearing(f.key); setError(null);
        try {
            for (const toolId of group.toolIds) {
                // eslint-disable-next-line no-await-in-loop
                await pluginsApi.clear(pluginId, groupId, { settings: [f.key], toolId: toolId || null });
            }
            onValue(f.key, f.type === 'boolean' ? false : '');
            onCleared?.(`${f.label || f.key} cleared.`);
        } catch (e) { setError(e?.message || 'Could not clear it'); } finally { setClearing(null); }
    };
    return (
        <div className="plugins-setgroup">
            {heading && <span className="plugins-sub">{heading}</span>}
            {group.fields.map((f) => (
                <div key={f.key} className="plugins-fieldwrap">
                    <Field field={f} groupId={groupId} value={values[f.key]} disabled={!gate.isAdmin} onChange={(v) => onValue(f.key, v)} />
                    {!blank(f.value) && f.type !== 'boolean' && <ClearLink label={f.label || f.key} busy={clearing === f.key} disabled={!gate.isAdmin} hint={gate.hint} onClear={() => clear(f)} />}
                </div>
            ))}
            {error && <p className="plugins-error" role="alert">{error}</p>}
        </div>
    );
}

// ── the single-step editors (a done Setup row) ───────────────────────────────
// `onSaved(message, { keep })`: keep — the sheet stays open (a Clear).

export function ConnectionSheet({ pluginId, groupId, name, oauth, gate, onConnect, onClose, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const conn = oauth?.connection || null;
    const disconnect = useDisconnect({ pluginId, groupId, name, oauth, onChanged });
    const connect = async () => {
        setBusy(true); setError(null);
        try { await onConnect(); } catch (e) { setError(e?.message || 'Could not start signing in'); setBusy(false); }
    };
    const leave = async () => {
        setBusy(true);
        try { if (await disconnect()) onClose(); } catch (e) { setError(e?.message || 'Could not disconnect'); } finally { setBusy(false); }
    };
    const held = gate.isAdmin ? undefined : gate.hint;
    return (
        <Modal open onClose={onClose} title={`${oauth?.provider_label || name} account`} size="md">
            <div className="plugins-form">
                <AccountPart oauth={oauth} gate={gate} />
                {error && <p className="plugins-error" role="alert">{error}</p>}
                {held && <p className="plugins-held" id="plugins-conn-held">{held}</p>}
                <SheetFoot left={conn?.connected ? <FootButton danger disabled={busy || !gate.isAdmin} title={held} aria-describedby={held ? 'plugins-conn-held' : undefined} onClick={leave}>Disconnect</FootButton> : null}>
                    <FootButton onClick={onClose}>Close</FootButton>
                    <FootButton primary busy={busy} disabled={busy || !gate.isAdmin} title={held} aria-describedby={held ? 'plugins-conn-held' : undefined} onClick={connect}>
                        {conn?.connected || conn?.revoked ? 'Sign in again' : 'Sign in'}
                    </FootButton>
                </SheetFoot>
            </div>
        </Modal>
    );
}

export function CredentialsSheet({ pluginId, groupId, fields, reenter = [], publicKey, gate, onClose, onSaved, onApprove }) {
    const [values, setValues] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const entered = Object.entries(values).filter(([, v]) => !blank(v));
    const save = async () => {
        setBusy(true); setError(null);
        try {
            const { approve } = await saveCredentials(pluginId, groupId, fields, values, publicKey);
            setValues({}); // the plaintext has no further use here
            onSaved?.('Keys saved.');
            if (approve) onApprove?.();
        } catch (e) {
            setError(e?.message || 'Could not save');
            setBusy(false);
        }
    };
    const held = gate.isAdmin ? undefined : gate.hint;
    return (
        <Modal open onClose={onClose} title="Keys" size="md">
            <div className="plugins-form">
                <CredentialsPart pluginId={pluginId} groupId={groupId} fields={fields} values={values} reenter={reenter} publicKey={publicKey} gate={gate}
                    onValue={(k, v) => setValues((cur) => ({ ...cur, [k]: v }))} onCleared={(m) => onSaved?.(m, { keep: true })} />
                {error && <p className="plugins-error" role="alert">{error}</p>}
                {held && <p className="plugins-held">{held}</p>}
                <SheetFoot>
                    <FootButton onClick={onClose}>Cancel</FootButton>
                    <FootButton primary busy={busy} disabled={busy || !entered.length || !gate.isAdmin || !publicKey} title={held} onClick={save}>Save</FootButton>
                </SheetFoot>
            </div>
        </Modal>
    );
}

export function SettingsSheet({ pluginId, groupId, group, title = 'Settings', gate, onClose, onSaved, onApprove }) {
    const [values, setValues] = useState(() => settingValues(group));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const save = async () => {
        setBusy(true); setError(null);
        try {
            const { approve } = await saveSettings(pluginId, groupId, group, values);
            onSaved?.('Settings saved.');
            if (approve) onApprove?.();
        } catch (e) {
            setError(e?.message || 'Could not save');
            setBusy(false);
        }
    };
    const held = gate.isAdmin ? undefined : gate.hint;
    return (
        <Modal open onClose={onClose} title={title} size="md">
            <div className="plugins-form">
                <SettingsPart pluginId={pluginId} groupId={groupId} group={group} values={values} gate={gate}
                    onValue={(k, v) => setValues((cur) => ({ ...cur, [k]: v }))} onCleared={(m) => onSaved?.(m, { keep: true })} />
                {error && <p className="plugins-error" role="alert">{error}</p>}
                {held && <p className="plugins-held">{held}</p>}
                <SheetFoot>
                    <FootButton onClick={onClose}>Cancel</FootButton>
                    <FootButton primary busy={busy} disabled={busy || !gate.isAdmin} title={held} onClick={save}>Save</FootButton>
                </SheetFoot>
            </div>
        </Modal>
    );
}
